import React, { useMemo } from 'react'
import Select from 'react-select'
import { SignatureConnexion } from '../Types/Class/SignatureConnexion'
import { People } from '../../../../types/People'
import { Site } from '../../../../types/Site'
import { SelectOption } from '../../../../types/SelectOption'

interface ConnectionsSectionProps {
    connections: SignatureConnexion[]
    sites: Site[]
    users: People[]
    testingConnection: number | null
    handleUpdateConnection: (siteId: number, field: keyof SignatureConnexion, value: any) => void
    handleTestConnection: (siteId: number, appName: string) => Promise<void>
    handleToggleConnection: (siteId: number) => void
    getConnectionToggleClass: (status?: 'inactive' | 'active' | 'error') => string
    appName: string
}

export default function ConnectionsSection({
    connections,
    sites,
    users,
    testingConnection,
    handleUpdateConnection,
    handleTestConnection,
    handleToggleConnection,
    getConnectionToggleClass,
    appName
}: ConnectionsSectionProps) {
    const userOptions: SelectOption[] = useMemo(() =>
        users.map(user => ({
            label: user.FullName ||  `${user.Lastname} ${user.Firstname.charAt(0).toUpperCase() + user.Firstname.slice(1).toLowerCase()}`,
            value: user.IDPeople
        }))
    , [users])

    const getUserOption = (idPeople: number | undefined): SelectOption | null => {
        if (!idPeople) return null
        return userOptions.find(opt => opt.value === idPeople) || null
    }

    return (
        <div className="card mb-4">
            <div className="card-header">
                <h4>Connexion à Oodrive</h4>
            </div>
            <div className="card-body">
                <table className="table table-bordered table-sm">
                    <thead className="thead-light">
                        <tr>
                            <th>Site</th>
                            <th>Représentant</th>
                            <th>Token</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {connections.map(connection => {
                            const site = sites.find(s => s.IDSite === connection.IDSite)
                            const canTest = connection.IDPeopleRepresentant && connection.Token
                            const canToggle = connection.IDPeopleRepresentant && connection.Token

                            return (
                                <tr key={connection.IDSite} style={{ opacity: connection.EstActif ? 1 : 0.5 }}>
                                    <td>{site?.Descr}</td>
                                    <td style={{ minWidth: '200px' }}>
                                        <Select
                                            options={userOptions}
                                            value={getUserOption(connection.IDPeopleRepresentant)}
                                            onChange={(option: SelectOption | null) => handleUpdateConnection(
                                                connection.IDSite,
                                                'IDPeopleRepresentant',
                                                option ? option.value : undefined
                                            )}
                                            placeholder="Utilisateur"
                                            searchable={true}
                                            clearable={true}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="JToken Oodrive"
                                            value={connection.Token || ''}
                                            onChange={(e) => {
                                                const newToken = e.target.value
                                                const isModified = newToken !== connection.OriginalToken
                                                handleUpdateConnection(connection.IDSite, 'Token', newToken)
                                                handleUpdateConnection(connection.IDSite, 'IsTokenModified', isModified)
                                                if (isModified) {
                                                    handleUpdateConnection(connection.IDSite, 'TestStatus', 'inactive')
                                                } else {
                                                    handleUpdateConnection(connection.IDSite, 'TestStatus', 'active')
                                                }
                                            }}
                                        />
                                    </td>
                                    <td>
                                        {connection.EstActif && canTest && connection.IsTokenModified && (
                                            <button
                                                className={`btn btn-sm ${getConnectionToggleClass(connection.TestStatus)}`}
                                                onClick={() => handleTestConnection(connection.IDSite, appName)}
                                                disabled={testingConnection === connection.IDSite}
                                            >
                                                {testingConnection === connection.IDSite ? (
                                                    <>
                                                        <i className="fa fa-spinner fa-spin"></i>
                                                        <span className="ml-2">Test...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        Test
                                                        {connection.TestStatus === 'active' && (
                                                            <i className="fa fa-check ml-2"></i>
                                                        )}
                                                        {connection.TestStatus === 'error' && (
                                                            <i className="fa fa-times ml-2"></i>
                                                        )}
                                                    </>
                                                )}
                                            </button>
                                        )}
                                        <button
                                            className={`btn btn-sm ml-2 ${ connection.EstActif && canTest ? 'btn-success' : 'btn-warning' }`}
                                            onClick={() => handleToggleConnection(connection.IDSite)}
                                            disabled={!canToggle}
                                        >
                                            <i className={`fa ${connection.EstActif && canTest ? 'fa-toggle-on' : 'fa-toggle-off'} mr-1`}></i>
                                            {connection.EstActif && canTest? 'Activée' : 'Désactivée'}
                                        </button>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
